import { useSearchParams } from 'react-router-dom';
import { FieldColorTokens } from './tokens/field.color.tokens';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useAppSelector } from '../../../../redux';

const setSourceData = (map, sourceId, features, parsed = false) => {
  if (!map || !map.getSource(sourceId)) {
    return;
  }
  if (parsed) {
    map.getSource(sourceId).setData(features);
    return;
  }
  const geojson = {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: features,
    },
  };
  map.getSource(sourceId).setData(geojson.data);
};

function AgoroGeoJSONList(): JSX.Element {
  const map = useAppSelector((state) => state.map.map);
  const fields = useAppSelector((state) => state.map.fields);
  const Pfields = useAppSelector((state) => state.map.processedFields);
  const trainingRegions = useAppSelector((state) => state.map.trainingRegions);
  const showTrainigngRegions = useAppSelector((state) => state.map.showTrainigngRegions);
  const classification = useAppSelector((state) => state.map.classificationLayers);
  const selectedLayer = useAppSelector((state) => state.map.classificationLayer);
  const paddocks = useAppSelector((state) => state.map.paddocks);
  const nearFields = useAppSelector((state) => state.map.nearFields);
  const showCloserFields = useAppSelector((state) => state.map.showCloserFields);

  const [searchParams] = useSearchParams();
  const fieldId = searchParams.get('fieldId');
  const { hideGrazing, enableAsyncFieldProcessing } = useFlags();

  const fieldList: any = [];
  const trainigList: any = [];
  const paddockList: any = [];
  const nearFieldsList: any = [];
  const wetlandsList: any = [];
  const publicLandsList: any = [];

  if (trainingRegions.length) {
    trainingRegions.forEach((region) => {
      trainigList.push(structuredClone(region));
    });
  }

  function addFieldPaddocks(fieldId: string | number, colors) {
    paddocks[fieldId].forEach((paddock) => {
      if (!paddock?.properties) return;
      paddock.properties.fillColor = colors.fillColor;
      paddock.properties.fillOpacity = colors.fillOpacity;
      paddock.properties.lineColor = colors.lineColor;
      paddock.properties.lineWidth = colors.lineWidth;

      paddockList.push(paddock);
    });
  }

  if (fields && fields.length > 0) {
    fields.forEach((field) => {
      if (!field?.properties) return;
      if (fieldId && field.id !== fieldId) return;
      if (field.properties!.hidden) return;
      if (!field.properties['editing']) {
        let colors: any = FieldColorTokens.fieldUnselected;
        if (field.properties.unenrolledAt) colors = FieldColorTokens.fieldUnenrolled;
        if (field.properties.selected) colors = FieldColorTokens.fieldSelected;
        if (field.properties.intersects || field.properties.overlaps) colors = FieldColorTokens.fieldIntersection;
        if (!hideGrazing && field.id && paddocks?.[field.id]?.length > 1) {
          addFieldPaddocks(field.id, structuredClone(colors));
          colors = {
            ...colors,
            lineWidth: 0,
            fillOpacity: 0,
          };
        }

        const fieldCopy = structuredClone(field);
        fieldList.push({
          ...fieldCopy,
          properties: {
            ...fieldCopy.properties,
            ...colors,
          },
        });

        if (enableAsyncFieldProcessing && !field.properties?.ineligibleAreasFinalized) {
          const processedProperties = field.properties?.processedIneligibleBoundaries ?? null;
          if (processedProperties?.wetlands?.area > 0) {
            for (const wetland of processedProperties.wetlands.boundaries) {
              const wl = {
                id: `${field.id}_${wetland.id}`,
                geometry: wetland.intersection,
                subType: wetland.sub_type,
                type: wetland.type,
                properties: {
                  name: wetland.sub_type,
                  lineColor: FieldColorTokens.wetland.lineColor,
                  lineWidth: FieldColorTokens.wetland.lineWidth,
                  fillColor: FieldColorTokens.wetland.fillColor,
                  fillOpacity: FieldColorTokens.wetland.fillOpacity,
                },
              };
              wetlandsList.push(wl);
            }
          }
          if (processedProperties?.publicLands?.area > 0) {
            for (const publicLand of processedProperties.publicLands.boundaries) {
              const pl = {
                id: `${field.id}_${publicLand.id}`,
                geometry: publicLand.intersection,
                subType: publicLand.sub_type,
                type: publicLand.type,
                properties: {
                  name: 'Public Land',
                  lineColor: FieldColorTokens.publicLand.lineColor,
                  lineWidth: FieldColorTokens.publicLand.lineWidth,
                  fillColor: FieldColorTokens.publicLand.fillColor,
                  fillOpacity: FieldColorTokens.publicLand.fillOpacity,
                },
              };
              publicLandsList.push(pl);
            }
          }
        }
      }
    });
  } else {
    fieldList.push(
      ...Pfields.map((field) => {
        return {
          ...field,
          properties: {
            ...FieldColorTokens.fieldUnselected,
          },
        };
      }),
    );
  }

  if (nearFields?.length && showCloserFields) {
    for (const fieldItem of nearFields) {
      const field = structuredClone(fieldItem);
      if (field.type === 'INELIGIBLE_AREA') {
        field.boundaries.properties.lineColor = FieldColorTokens.wetland.lineColor;
        field.boundaries.properties.lineWidth = FieldColorTokens.wetland.lineWidth;
        field.boundaries.properties.fillColor = FieldColorTokens.wetland.fillColor;
        field.boundaries.properties.fillOpacity = FieldColorTokens.wetland.fillOpacity;
        if (!wetlandsList.find((i) => i.id === field.id)) {
          wetlandsList.push(field.boundaries);
        }
      } else {
        field.boundaries.properties.lineColor = !field.unenrolledAt
          ? FieldColorTokens.nearField.lineColor
          : FieldColorTokens.fieldUnenrolled.lineColor;
        field.boundaries.properties.lineWidth = FieldColorTokens.nearField.lineWidth;
        field.boundaries.properties.fillColor = !field.unenrolledAt
          ? FieldColorTokens.nearField.fillColor
          : FieldColorTokens.fieldUnenrolled.fillColor;
        field.boundaries.properties.fillOpacity = FieldColorTokens.nearField.fillOpacity;
        if (!nearFieldsList.find((i) => i.id === field.id)) {
          nearFieldsList.push(field.boundaries);
        }
      }
    }
  }
  if (map && map.getSource('wetlands')) {
    setSourceData(map, 'wetlands', wetlandsList);
    setSourceData(map, 'public-lands', publicLandsList);
    setSourceData(map, 'near-fields', nearFieldsList);
    setSourceData(map, 'fields', fieldList);
    setSourceData(map, 'training-regions', showTrainigngRegions ? trainigList : []);
    setSourceData(
      map,
      'cdl_landcover',
      classification && selectedLayer?.value === 'cdl_landcover' ? classification.cdl_landcover : [],
      selectedLayer?.value === 'cdl_landcover',
    );
    setSourceData(
      map,
      'classified',
      classification && selectedLayer?.value === 'classified' ? classification.classified : [],
      selectedLayer?.value === 'classified',
    );
    setSourceData(
      map,
      'classified_dw',
      classification && selectedLayer?.value === 'classified_dw' ? classification.classified_dw : [],
      selectedLayer?.value === 'classified_dw',
    );
    setSourceData(
      map,
      'esa_landcover',
      classification && selectedLayer?.value === 'esa_landcover' ? classification.esa_landcover : [],
      selectedLayer?.value === 'esa_landcover',
    );
    setSourceData(
      map,
      'hull-Tree-clustered',
      classification && selectedLayer?.value === 'hull-Tree-clustered' ? classification['hull-Tree-clustered'] : [],
      selectedLayer?.value === 'hull-Tree-clustered',
    );
    if (!hideGrazing) setSourceData(map, 'paddocks', paddockList);
  }

  return <div />;
}

export default AgoroGeoJSONList;
